<template>
  <div v-if="visible" class="sbanner" role="status">
    <div class="sbanner__in">
      <span class="sbanner__tag">{{ sale.label || 'Sale' }}</span>
      <span class="sbanner__what">{{ headline }}</span>
      <span v-if="countdown" class="sbanner__timer">
        <span class="sbanner__timer-label">ends in</span>
        <span class="sbanner__timer-value">{{ countdown }}</span>
      </span>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

const props = defineProps({
  /** The `sale` block from any shop endpoint. Null when nothing is running. */
  sale: { type: Object, default: null }
})

const headline = computed(() => {
  const mp3 = Number(props.sale?.percentMp3) || 0
  const wav = Number(props.sale?.percentWav) || 0
  // One number when both formats match — "20% off MP3 · 20% off WAV" is the
  // same sale said twice, and the shopper has to read it to find that out.
  //
  // Deliberately NOT "off all downloads": a storefront that mirrors a peered
  // partner's catalogue sells items the sale does not touch, so "all" would be
  // a claim the price tags contradict. The per-item badges say which ones.
  if (mp3 > 0 && wav > 0 && mp3 === wav) return `${mp3}% off MP3 & WAV`
  const parts = []
  if (mp3 > 0) parts.push(`${mp3}% off MP3`)
  if (wav > 0) parts.push(`${wav}% off WAV`)
  return parts.join(' · ')
})

const endsAtMs = computed(() => {
  if (!props.sale?.endsAt) return null
  const ms = new Date(props.sale.endsAt).getTime()
  return Number.isNaN(ms) ? null : ms
})

// Ticks client-side so the banner disappears the moment the sale ends, without
// the visitor reloading. SSR renders the static text only.
//
// Only started when there IS an end date. An open-ended sale has nothing to
// count down and nothing to expire, so a 1 Hz timer there would re-run these
// computeds on every shop page for the entire visit and buy nothing.
const now = ref(null)
let timer = null

onMounted(() => {
  if (endsAtMs.value === null) return
  now.value = Date.now()
  timer = setInterval(() => { now.value = Date.now() }, 1000)
})
onBeforeUnmount(() => { if (timer) clearInterval(timer) })

const visible = computed(() => {
  if (!props.sale || !headline.value) return false
  // Before hydration `now` is null — trust the server, which already resolved
  // the window. After hydration, hide an expired sale even if the page was
  // rendered while it was still running.
  if (now.value === null || endsAtMs.value === null) return true
  return endsAtMs.value > now.value
})

const countdown = computed(() => {
  if (endsAtMs.value === null || now.value === null) return null
  const remaining = Math.max(0, endsAtMs.value - now.value)
  if (remaining <= 0) return null
  const total = Math.floor(remaining / 1000)
  const days = Math.floor(total / 86400)
  const hours = Math.floor((total % 86400) / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = total % 60
  const pad = (n) => String(n).padStart(2, '0')
  // Past a day the seconds are noise; inside a day they are the urgency.
  if (days > 0) return `${days}d ${pad(hours)}h ${pad(minutes)}m`
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
})
</script>

<style scoped>
.sbanner {
  border: 1px solid var(--cy-alarm-dim);
  border-radius: 4px;
  background:
    linear-gradient(90deg,
      color-mix(in srgb, var(--accent) 14%, transparent) 0%,
      color-mix(in srgb, var(--accent) 4%, transparent) 40%,
      transparent 100%);
  padding: 10px 14px;
}

.sbanner__in {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.sbanner__tag {
  font-family: var(--cy-mono);
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--bg-color);
  background: var(--accent);
  border-radius: 2px;
  padding: 3px 8px;
  white-space: nowrap;
}

.sbanner__what {
  font-family: var(--cy-display);
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--text-main);
}

.sbanner__timer {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  margin-left: auto;
}

.sbanner__timer-label {
  font-family: var(--cy-mono);
  font-size: 0.58rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--text-dim);
}

.sbanner__timer-value {
  font-family: var(--cy-mono);
  font-variant-numeric: tabular-nums;
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--accent);
}

@media (max-width: 480px) {
  .sbanner__timer { margin-left: 0; width: 100%; }
}
</style>
